import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { ArrowLeft, Phone, Mail, MapPin, Clock, MessageCircle, Instagram } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Seo from '../components/Seo';
import { pickMeta } from '../seoMeta';

function Contacts({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const meta = pickMeta('contacts', currentLang);

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo lang={currentLang} path="contacts" title={meta.title} description={meta.description} />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to={`/${currentLang}`} className="flex items-center">
              <img 
                src="/logo.jpg" 
                alt="Ainanan" 
                className="h-10 object-contain"
              />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to={`/${currentLang}`} className="text-sm font-medium text-gray-600 hover:text-stone-700">{t('common.catalog')}</Link>
              <Link to={`/${currentLang}/about`} className="text-sm font-medium text-gray-600 hover:text-stone-700">{t('common.about')}</Link>
              <Link to={`/${currentLang}/contacts`} className="text-sm font-medium text-gray-900">{t('common.contacts')}</Link>
            </nav>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              {user ? (
                <Button variant="ghost" size="sm" onClick={onLogout}>{t('common.logout')}</Button>
              ) : (
                <Link to={`/${currentLang}/login`}>
                  <Button variant="ghost" size="sm">{t('common.login')}</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <Link to={`/${currentLang}`} className="inline-flex items-center text-stone-700 hover:text-stone-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('contacts.backToCatalog')}
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-6">{t('contacts.heading')}</h1>
        
        <p className="text-xl text-gray-600 mb-8">
          {t('contacts.intro')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Phone */}
          <a 
            href="https://wa.me/77010982754" 
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white p-6 rounded-xl border hover:shadow-md transition-shadow flex items-start gap-4"
          >
            <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Phone className="w-6 h-6 text-stone-700" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">{t('contacts.phone')}</h3>
              <p className="text-lg text-stone-700 font-medium">+7 701 098 27 54</p>
              <p className="text-sm text-gray-500">{t('contacts.phoneHours')}</p>
            </div>
          </a>

          {/* WhatsApp */}
          <a 
            href="https://wa.me/77010982754" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-white p-6 rounded-xl border hover:shadow-md transition-shadow flex items-start gap-4"
          >
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">{t('contacts.whatsapp')}</h3>
              <p className="text-lg text-green-600 font-medium">+7 701 098 27 54</p>
              <p className="text-sm text-gray-500">{t('contacts.whatsappDesc')}</p>
            </div>
          </a>

          {/* Email */}
          <a 
            href="mailto:admin@ainanan.kz" 
            className="bg-white p-6 rounded-xl border hover:shadow-md transition-shadow flex items-start gap-4"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">{t('contacts.email')}</h3>
              <p className="text-lg text-blue-600 font-medium">admin@ainanan.kz</p>
              <p className="text-sm text-gray-500">{t('contacts.emailDesc')}</p>
            </div>
          </a>

          {/* Instagram */}
          <a 
            href="https://www.instagram.com/ainanan.kz/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-white p-6 rounded-xl border hover:shadow-md transition-shadow flex items-start gap-4"
          >
            <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Instagram className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Instagram</h3>
              <p className="text-lg text-pink-600 font-medium">@ainanan.kz</p>
              <p className="text-sm text-gray-500">Следите за новинками и акциями</p>
            </div>
          </a>

          {/* Working Hours */}
          <div className="bg-white p-6 rounded-xl border flex items-start gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">{t('contacts.workingHours')}</h3>
              <p className="text-gray-600">{t('contacts.weekdays')}</p>
              <p className="text-gray-600">{t('contacts.weekends')}</p>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white p-6 rounded-xl border mb-8">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <MapPin className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">{t('contacts.address')}</h3>
              <a 
                href="https://go.2gis.com/kFYI7" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-lg text-gray-700 hover:text-emerald-600 transition-colors"
              >
                г. Алматы, ул. Баранского 15/1
              </a>
            </div>
          </div>
          
          {/* 2GIS Map Link */}
          <a 
            href="https://go.2gis.com/kFYI7" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block w-full h-64 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg overflow-hidden hover:from-emerald-100 hover:to-emerald-200 transition-all border border-emerald-200 flex items-center justify-center group"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <p className="text-emerald-800 font-semibold text-lg">{t('contacts.openIn2GIS') || 'Открыть в 2GIS'}</p>
              <p className="text-emerald-600 text-sm mt-1">г. Алматы, ул. Баранского 15/1</p>
            </div>
          </a>
        </div>

        {/* B2B Contact - Wholesale Orders */}
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-stone-800 mb-2">{t('contacts.b2bTitle')}</h3>
          <p className="text-stone-700 mb-4">
            {t('contacts.b2bDesc')}
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="https://wa.me/77010982754" target="_blank" rel="noopener noreferrer">
              <Button className="bg-emerald-700 hover:bg-emerald-800">
                <Phone className="w-4 h-4 mr-2" />
                +7 701 098 27 54
              </Button>
            </a>
            <a href="mailto:admin@ainanan.kz">
              <Button variant="outline" className="border-stone-600 text-stone-700 hover:bg-stone-50">
                <Mail className="w-4 h-4 mr-2" />
                admin@ainanan.kz
              </Button>
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default Contacts;
